// Per-grass pros/cons cards. Pure presentational; data comes from
// data/grass-comparison-ui.ts.

import { Check, X } from 'lucide-react'
import { GrassCharacteristics } from '@/lib/types/recommendation-engine'
import { getGrassComparisonUi } from '@/data/grass-comparison-ui'
import { cn } from '@/lib/utils'

interface ProsConsCardsProps {
  grasses: GrassCharacteristics[]
}

export function ProsConsCards({ grasses }: ProsConsCardsProps) {
  if (grasses.length < 2) return null

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 min-[720px]:gap-5',
        grasses.length === 2 ? 'min-[720px]:grid-cols-2' : 'min-[720px]:grid-cols-3',
      )}
    >
      {grasses.map((g) => {
        const ui = getGrassComparisonUi(g.name)
        return (
          <article
            key={g.name}
            className="rounded-2xl border border-gray-200 bg-white p-5 shadow-elevation-2 sm:p-6"
          >
            <header className="mb-3 flex items-center gap-2.5">
              <span
                className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-white"
                style={{ backgroundColor: ui.accentColor }}
                aria-hidden="true"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M5 21h14M8 21c0-5 1-8 3-10M12 21c0-7 2-10 4-13M16 21c0-4 1-6 2-8" />
                </svg>
              </span>
              <h3 className="text-base font-bold tracking-tight text-gray-900">{g.name}</h3>
            </header>
            {ui.bestFor && (
              <p className="mb-3 text-sm leading-relaxed text-gray-700">
                <strong className="text-gray-900">Best for:</strong> {ui.bestFor}
              </p>
            )}
            <ul className="space-y-1.5">
              {ui.pros.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  <span>{p}</span>
                </li>
              ))}
              {ui.cons.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-gray-500">
                  <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                    <X className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </article>
        )
      })}
    </div>
  )
}
